import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { permissionMiddleware } from "~/lib/permission-middleware";
import {
  adminClaimFiltersSchema,
  claimDecisionInputSchema,
  type AdminClaim,
  type AdminClaimFilters,
} from "~/lib/shared/cco-claims";

// ============================================================================
// Query Keys
// ============================================================================

export const verificationQueryKeys = {
  claims: (filters: AdminClaimFilters) =>
    ["admin-verifications", "claims", filters] as const,
  openCounts: ["admin-verifications", "open-counts"] as const,
};

// ============================================================================
// Server Functions
// ============================================================================

/**
 * Membership or program claims with their state, decider and date.
 * Rules live in `~/lib/server/cco-claims`.
 */
export const getAdminClaimsFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("coaches:read")])
  .validator(adminClaimFiltersSchema)
  .handler(async ({ data }): Promise<AdminClaim[]> => {
    const { listClaimsForStaff } = await import("~/lib/server/cco-claims");
    return listClaimsForStaff(data);
  });

/**
 * Staff verify claims (with an optional date) or mark them not confirmed,
 * as Dream Coach Match. Answering a review request emails its outcome.
 */
export const adminDecideClaimsFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("coaches:write")])
  .validator(claimDecisionInputSchema)
  .handler(async ({ data, context }): Promise<{ decided: number }> => {
    const { decideClaims } = await import("~/lib/server/cco-claims");
    return decideClaims({
      actor: { role: "staff", userId: context.user.id },
      ...data,
    });
  });

/** Claims waiting for a decision and open review requests. */
export const getOpenClaimCountsFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("coaches:read")])
  .handler(
    async (): Promise<{
      waitingMemberships: number;
      waitingPrograms: number;
      reviewRequests: number;
    }> => {
      const { countOpenClaims } = await import("~/lib/server/cco-claims");
      return countOpenClaims();
    },
  );

/**
 * Create a new CCO membership (coach-CCO association).
 * Aliases: add cco membership, assign coach to cco
 */
export const createCcoMembershipFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("coaches:write")])
  .validator(
    z.object({
      coachId: z.string().uuid(),
      ccoId: z.string().uuid(),
    }),
  )
  .handler(async ({ data }): Promise<{ success: boolean }> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { coachesCcos } = await import("~/../drizzle/schema");

    const db = await getDBWithInit();

    await db.insert(coachesCcos).values({
      coachId: data.coachId,
      ccoId: data.ccoId,
    });

    return { success: true };
  });

/**
 * Create a new CTP certification (coach-CTP association).
 * Aliases: add ctp certification, assign coach to ctp
 */
export const createCtpCertificationFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("coaches:write")])
  .validator(
    z.object({
      coachId: z.string().uuid(),
      ctpId: z.string().uuid(),
    }),
  )
  .handler(async ({ data }): Promise<{ success: boolean }> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { coachesCtps } = await import("~/../drizzle/schema");

    const db = await getDBWithInit();

    await db.insert(coachesCtps).values({
      coachId: data.coachId,
      ctpId: data.ctpId,
    });

    return { success: true };
  });

/**
 * Search coaches by name or email for the create verification dropdown.
 * Returns a lightweight list of {id, fullName, email}.
 * Aliases: coach search, coach dropdown, coach autocomplete
 */
export const searchCoachesFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("coaches:read")])
  .validator(
    z.object({
      search: z.string().min(1),
    }),
  )
  .handler(
    async ({
      data,
    }): Promise<{ id: string; fullName: string; email: string }[]> => {
      const { getDBWithInit } = await import("~/lib/db");
      const { coaches } = await import("~/../drizzle/schema");
      const { ilike, or } = await import("drizzle-orm");

      const db = await getDBWithInit();
      const term = `%${data.search}%`;

      const rows = await db
        .select({
          id: coaches.id,
          fullName: coaches.fullName,
          email: coaches.email,
        })
        .from(coaches)
        .where(or(ilike(coaches.fullName, term), ilike(coaches.email, term)))
        .limit(20);

      return rows;
    },
  );

/**
 * Delete a CCO membership (coach-CCO association).
 * Aliases: remove cco membership, unassign coach from cco
 */
export const deleteCcoMembershipFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("coaches:write")])
  .validator(
    z.object({
      coachId: z.string().uuid(),
      ccoId: z.string().uuid(),
    }),
  )
  .handler(async ({ data }): Promise<{ success: boolean }> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { coachesCcos } = await import("~/../drizzle/schema");
    const { eq, and } = await import("drizzle-orm");

    const db = await getDBWithInit();
    await db
      .delete(coachesCcos)
      .where(
        and(
          eq(coachesCcos.coachId, data.coachId),
          eq(coachesCcos.ccoId, data.ccoId),
        ),
      );
    return { success: true };
  });

/**
 * Delete a CTP certification (coach-CTP association).
 * Aliases: remove ctp certification, unassign coach from ctp
 */
export const deleteCtpCertificationFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("coaches:write")])
  .validator(
    z.object({
      coachId: z.string().uuid(),
      ctpId: z.string().uuid(),
    }),
  )
  .handler(async ({ data }): Promise<{ success: boolean }> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { coachesCtps } = await import("~/../drizzle/schema");
    const { eq, and } = await import("drizzle-orm");

    const db = await getDBWithInit();
    await db
      .delete(coachesCtps)
      .where(
        and(
          eq(coachesCtps.coachId, data.coachId),
          eq(coachesCtps.ctpId, data.ctpId),
        ),
      );
    return { success: true };
  });

/**
 * Delete a coach-framework association.
 * Aliases: remove coach framework, unassign coach from framework
 */
export const deleteCoachFrameworkFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("coaches:write")])
  .validator(
    z.object({
      coachId: z.string().uuid(),
      frameworkId: z.string().uuid(),
    }),
  )
  .handler(async ({ data }): Promise<{ success: boolean }> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { coachesFrameworks } = await import("~/../drizzle/schema");
    const { eq, and } = await import("drizzle-orm");

    const db = await getDBWithInit();
    await db
      .delete(coachesFrameworks)
      .where(
        and(
          eq(coachesFrameworks.coachId, data.coachId),
          eq(coachesFrameworks.frameworkId, data.frameworkId),
        ),
      );
    return { success: true };
  });

// ============================================================================
// Query Options
// ============================================================================

export const openClaimCountsQueryOptions = queryOptions({
  queryKey: verificationQueryKeys.openCounts,
  queryFn: () => getOpenClaimCountsFn(),
});
